// ============================================================================
// SYUT ACTIVITY SELECT
// ============================================================================
// Domain wrapper around SyutSelect for the Activity master.
//
// Convention used across all entity selects:
//   - defaultOptions={true} → loads up to `pageSize` rows on focus.
//   - minSearchLength={3}   → typed search triggers a server fetch only after
//                             the user enters 3 or more characters. This keeps
//                             the dropdown responsive for large masters.
//   - cacheOptions={false}  → no client-side cache so newly created activities
//                             appear immediately.
//
// The Timesheet entry popup needs to filter activities by:
//   1. chargeTypeKey (C / NCP / NCNP / D)  — backend filter
//   2. engagementTypeId — backend filter via the engagement_type_id mapping table
// Both are passed straight through to activityService.getAll.

import React from 'react';
import { SyutSelect } from '../SyutSelect';
import { activityService } from 'api/services/activityService';

export interface SyutActivitySelectProps {
  value?: string;
  onChange: (value: string) => void;
  label?: React.ReactNode;
  error?: string;
  required?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Seed options used to resolve a saved value's label in edit mode. */
  seedOptions?: { value: string; label: string }[];
  /** Server-side filter — restricts results to a single charge type (C / NCP / NCNP / D). */
  chargeTypeKey?: string;
  /** Server-side filter — restricts results to activities mapped to this engagement type. */
  engagementTypeId?: string;
  includeInactive?: boolean;
  /** Default-load and search page size (default 500). */
  pageSize?: number;
  disabled?: boolean;
}

export const SyutActivitySelect: React.FC<SyutActivitySelectProps> = ({
  value,
  onChange,
  label,
  error,
  required,
  isClearable = true,
  placeholder = 'Type 3 letters to search activities...',
  size = 'sm',
  seedOptions,
  chargeTypeKey,
  engagementTypeId,
  includeInactive = false,
  pageSize = 500,
  disabled,
}) => (
  <SyutSelect
    label={label}
    value={value}
    isAsync
    loadOptions={async (input: string) => {
      const res = await activityService.getAll({
        page: 1,
        pageSize,
        searchTerm: input || undefined,
        chargeTypeKey: chargeTypeKey || undefined,
        engagementTypeId: engagementTypeId || undefined,
        includeInactive,
      });
      return res.data.map((a: any) => ({
        value: a.id,
        label: `${a.code ? a.code + ' - ' : ''}${a.name || ''}`.trim() || a.id,
      }));
    }}
    defaultOptions
    cacheOptions={false}
    seedOptions={seedOptions}
    debounceDelay={300}
    minSearchLength={3}
    isClearable={isClearable}
    size={size}
    placeholder={placeholder}
    error={error}
    required={required}
    isDisabled={disabled}
    onChange={(val) => onChange((val as string) || '')}
  />
);

export default SyutActivitySelect;
